/**
 * Apple-style scroll reveals. Headings, text, images and grid cards below the
 * first screen rise into place and come into focus as they scroll in; cards in
 * a row ripple in one after another.
 *
 * Safety rules so content always shows:
 * - Nothing on the first screen (or above it) is ever hidden; the page is
 *   measured after fonts and layout settle.
 * - Anything that reaches the screen reveals, however tall it is.
 * - A scroll/resize check reveals anything on screen the observer missed,
 *   and everything reveals if the page is printed or motion is reduced.
 */
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const main = document.getElementById('main-content');

const start = () => {
  if (!main || reduce.matches || !('IntersectionObserver' in window)) return;

  const SKIP = '[data-drift], dialog, [popover], s-sticky-buy, s-hero-video, [data-product-gallery], [class*="overflow-x-auto"], [data-reveal-skip], .sr-only, form, details, header';
  const UNIT = [
    ':is(ul, ol)[role="list"] > li',
    '.grid > *',
    'h1, h2, h3',
    'p',
    'img, video, picture, [data-drift-frame]',
    '[data-reveal]',
  ].join(',');

  const units = new Set();
  const pending = new Set();
  const isUnder = (el) => {
    for (let p = el.parentElement; p && p !== main; p = p.parentElement) if (units.has(p)) return true;
    return false;
  };

  const reveal = (el) => {
    if (!pending.has(el)) return;
    pending.delete(el);
    io.unobserve(el);
    el.setAttribute('data-revealed', '');
    setTimeout(() => {
      el.style.removeProperty('--reveal-delay');
      el.removeAttribute('data-reveal-state');
      el.removeAttribute('data-revealed');
    }, 1600);
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => { if (entry.isIntersecting) reveal(entry.target); });
  }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });

  const addUnits = (root) => {
    const fold = window.innerHeight;
    const found = [];
    root.querySelectorAll(UNIT).forEach((el) => {
      if (el.closest(SKIP) || units.has(el) || isUnder(el)) return;
      if (el.matches('.grid > *') && el.parentElement.children.length < 2) return;
      const rect = el.getBoundingClientRect();
      if (rect.height === 0 || rect.width === 0) return;
      if (rect.top < fold) return; // first screen or above: always visible
      units.add(el);
      found.push(el);
    });
    found.forEach((el) => {
      const siblings = [...el.parentElement.children].filter((c) => units.has(c));
      const i = siblings.indexOf(el);
      el.style.setProperty('--reveal-delay', `${Math.min(i % 4, 3) * 90}ms`);
      el.setAttribute('data-reveal-state', el.matches('h1, h2, .type-display') ? 'heading' : 'pending');
      if (el.parentElement.classList.contains('stagger-fade-in')) el.parentElement.classList.remove('stagger-fade-in');
      pending.add(el);
      io.observe(el);
    });
  };

  // Backup check: reveal anything on screen the observer hasn't caught.
  let ticking = false;
  const sweep = () => {
    ticking = false;
    const fold = window.innerHeight;
    pending.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < fold && r.bottom > 0) reveal(el);
    });
  };
  const queueSweep = () => { if (!ticking) { ticking = true; requestAnimationFrame(sweep); } };
  window.addEventListener('scroll', queueSweep, { passive: true });
  window.addEventListener('resize', queueSweep, { passive: true });
  window.addEventListener('beforeprint', () => pending.forEach(reveal));
  reduce.addEventListener('change', () => pending.forEach(reveal));

  document.documentElement.classList.add('reveal-ready');
  addUnits(main);
  const footer = document.querySelector('footer');
  if (footer) addUnits(footer);
  queueSweep();

  new MutationObserver((records) => {
    records.forEach((r) => r.addedNodes.forEach((n) => {
      if (n.nodeType === 1 && !n.closest(SKIP)) addUnits(n.parentElement || n);
    }));
  }).observe(main, { childList: true, subtree: true });
};

// Measure after fonts and layout settle so first-screen text is never hidden.
const fontsReady = document.fonts?.ready ?? Promise.resolve();
Promise.race([fontsReady, new Promise((r) => setTimeout(r, 800))]).then(() => {
  requestAnimationFrame(() => requestAnimationFrame(start));
});
